import { Component, OnInit, ViewChild } from '@angular/core';
import { AnalyticsService, CourseAnalyticsTab, getDefaultDateRange } from '@shared/services/analytics.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { Course, ExperienceType } from '@shared/models/course.model';
import { CurrentHubService } from '@shared/services/currentHub.service';
import { Annotation, AnnotationType, HubMode } from '@shared/models';
import { HeaderActionButton } from '../page-header/page-header.component';
import { LinkService } from '@shared/services/link.service';
import { courseLabel, StringCase } from '@shared/utils/wording.utils';

const orderedTabs = [
  CourseAnalyticsTab.Overview,
  CourseAnalyticsTab.Assessment,
  CourseAnalyticsTab.Visitors,
  CourseAnalyticsTab.Content,
  CourseAnalyticsTab.Sessions,
  CourseAnalyticsTab.Quizzes
];

@Component({
  templateUrl: './course-analytics.component.html',
  styleUrls: ['./course-analytics.component.scss']
})
export class CourseAnalyticsComponent implements OnInit {

  public course: Course;
  public quizzes: Annotation[];
  date_from: Date;  // TODO: Default value 1 year
  date_to: Date;
  displayLogSessionId: string | null = null;
  public activeTab = CourseAnalyticsTab.Overview;
  public activeUserSearch = '';
  public pageHeaderAdditionalActions: HeaderActionButton[];
  public isAIExperience = true;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _courseAPIService: CoursesAPIService,
    private _router: Router,
    private _linkService: LinkService,
  ) {

  }

  ngOnInit(): void {
    const defaultRange = getDefaultDateRange();
    this.date_from = defaultRange.date_from;
    this.date_to = defaultRange.date_to;
    this._activatedRoute.paramMap.subscribe(async params => {
      const course_id = params.get('courseId');
      // Get course content
      const course = await this._courseAPIService.getCourseById(course_id).toPromise();
      const quizzes = await this._courseAPIService.getCourseAnnotationsByType(course_id, AnnotationType.quiz).toPromise();
      this.course = course;
      this.quizzes = quizzes;
      this.isAIExperience = course.experience_type === ExperienceType.ai;
      this.pageHeaderAdditionalActions = [{
        button: {
          icon: 'edit',
          label: `Edit ${courseLabel(this.course.learnspace.mode, false, StringCase.titlecase)}`
        },
        action: () => {
          const editorPath = this.isAIExperience ? 'simulations' : 'courses';
          const url = `${this.course.learnspace.hub.urlTitle}/${this.course.learnspace.id}/${editorPath}/${this.course.id}`;
          this._router.navigate([url]);
        }
      }, {
        button: {
          icon: 'fa-play',
          fontAwesomeIcon: true,
          label: 'Launch',
          raised: true
        },
        action: () => {
          this._linkService.launchCourse(this.course, false);
        }
      }]
    });
    this._activatedRoute.queryParams.subscribe(params => {
      this.activeTab = params.activeTab;
      this.activeUserSearch = params.activeUserSearch;
      if (params.timePickerMode && params.timePickerMode === 'forever') {
        this.date_from = null;
        this.date_to = null;
        return;
      }
      if (params.dateFrom) {
        this.date_from = new Date(parseInt(params.dateFrom))
      }
      if (params.dateTo) {
        this.date_to = new Date(parseInt(params.dateTo))
      }
    })
  }

  displayLog(event: { sessionId: string, userName: string }) {
    this.displayLogSessionId = event.sessionId;
  }

  displaySessions() {
    this.displayLogSessionId = null;
  }
  
  getSelectedIndex() {
    return orderedTabs.indexOf(this.activeTab) || 0;
  }

  onSelectedIndexChange(index: number): void {
    let newTab = CourseAnalyticsTab.Overview;
    if (index >= 0 && index < orderedTabs.length) {
      newTab = orderedTabs[index];
    }

    const params: Params = { activeTab: newTab };
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  onNavigateTab(event: { name: string, tab?: CourseAnalyticsTab }): void {
    const params: Params = { activeUserSearch: event.name, activeTab: event.tab || this.activeTab };
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }
}
